import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, Crown, Users, FileText, Activity, Shield, Send,
  Clock, CheckCircle, XCircle, Trash2, UserPlus, Eye, Mail, Settings
} from 'lucide-react';
import { channelApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'gold_trade', label: 'Gold Trade' },
  { id: 'oil_energy', label: 'Oil & Energy' },
  { id: 'commodities', label: 'Commodities' },
  { id: 'machinery', label: 'Machinery' },
];

const POST_TYPES = [
  { id: 'trade_deal', label: 'Trade Deal' },
  { id: 'mandate', label: 'Mandate' },
  { id: 'commodity_offer', label: 'Commodity Offer' },
  { id: 'document', label: 'Intelligence / Document' },
  { id: 'image', label: 'Image / Gallery' },
];

const POST_LABELS = [
  { id: 'hot_deal', label: 'Hot Deal', color: 'bg-red-100 text-red-700' },
  { id: 'limited_allocation', label: 'Limited Allocation', color: 'bg-purple-100 text-purple-700' },
  { id: 'closing_soon', label: 'Closing Soon', color: 'bg-amber-100 text-amber-800' },
  { id: 'direct_mandate', label: 'Direct Mandate', color: 'bg-blue-100 text-blue-700' },
  { id: 'eagles_verified', label: 'Eagles Verified', color: 'bg-green-100 text-green-700' },
];

const ChannelAdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberFilter, setMemberFilter] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showGrantAccess, setShowGrantAccess] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  // Post form
  const [postForm, setPostForm] = useState({
    title: '', content: '', post_type: 'trade_deal', category: 'gold_trade',
    is_premium: true, deal_value: '', origin: '', destination: '', tags: '',
    labels: [], send_notification: false, priority_alert: false, notify_audience: 'auto'
  });
  const [postSubmitting, setPostSubmitting] = useState(false);

  // Grant form
  const [grantForm, setGrantForm] = useState({ access_type: '30_days', custom_days: 60, reason: '' });

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePerms, setInvitePerms] = useState({ can_post: true, can_manage_users: false, can_grant_access: false });

  useEffect(() => { fetchAll(); }, [memberFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, membersRes, logsRes] = await Promise.all([
        channelApi.getStats(),
        channelApi.getMembers({ filter_type: memberFilter || undefined }),
        channelApi.getAuditLogs({ limit: 20 }),
      ]);
      setStats(statsRes.data);
      setMembers(membersRes.data.members || []);
      setAuditLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.content) return toast.error('Title and content required');
    setPostSubmitting(true);
    try {
      const res = await channelApi.createPost({
        ...postForm,
        tags: postForm.tags ? postForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      });
      const sent = res.data?.notifications_sent || 0;
      toast.success(`Post published!${sent > 0 ? ` ${sent} notifications sent.` : ''}`);
      setShowCreatePost(false);
      setPostForm({ title: '', content: '', post_type: 'trade_deal', category: 'gold_trade', is_premium: true, deal_value: '', origin: '', destination: '', tags: '', labels: [], send_notification: false, priority_alert: false, notify_audience: 'auto' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    } finally {
      setPostSubmitting(false);
    }
  };

  const handleGrantAccess = async (userId) => {
    try {
      await channelApi.grantAccess({ user_id: userId, ...grantForm });
      toast.success('Access granted!');
      setShowGrantAccess(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId) => {
    try {
      await channelApi.revokeAccess({ user_id: userId, reason: 'Revoked by admin' });
      toast.success('Access revoked');
      fetchAll();
    } catch (err) {
      toast.error('Failed to revoke access');
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail) return toast.error('Email required');
    try {
      const { data } = await channelApi.inviteAdmin({ email: inviteEmail, permissions: invitePerms });
      toast.success(`Invite created! Code: ${data.invite_code}`);
      setShowInvite(false);
      setInviteEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await channelApi.deletePost(postId);
      toast.success('Post deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const getMemberAccessLabel = (m) => {
    if (['silver', 'gold', 'vip'].includes(m.membership)) return { label: m.membership.toUpperCase(), color: 'bg-blue-100 text-blue-800' };
    if (m.granted_access?.active) return { label: 'GRANTED', color: 'bg-green-100 text-green-800' };
    return { label: 'FREE', color: 'bg-slate-100 text-slate-600' };
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded" />)}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A192F] rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0A192F]">Channel Admin</h1>
              <p className="text-xs text-slate-500">Manage posts, members & access</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowInvite(true)} data-testid="invite-admin-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Invite Admin
            </Button>
            <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white" size="sm" onClick={() => setShowCreatePost(true)} data-testid="create-post-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Post
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Total Posts', value: stats.total_posts, icon: FileText },
              { label: 'Premium Posts', value: stats.premium_posts, icon: Crown },
              { label: 'Paid Members', value: stats.paid_members, icon: Shield },
              { label: 'Granted Members', value: stats.granted_members, icon: CheckCircle },
              { label: 'Total Views', value: stats.total_views?.toLocaleString(), icon: Eye },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">{s.label}</span>
                </div>
                <p className="text-xl font-bold text-[#0A192F]">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="members">
          <TabsList className="mb-4">
            <TabsTrigger value="members" data-testid="tab-members"><Users className="w-4 h-4 mr-1" /> Members</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><Activity className="w-4 h-4 mr-1" /> Audit Log</TabsTrigger>
          </TabsList>

          {/* MEMBERS TAB */}
          <TabsContent value="members">
            <div className="bg-white border border-slate-200">
              <div className="p-4 border-b flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700 mr-2">Filter:</span>
                {['', 'paid', 'granted', 'free'].map(f => (
                  <Button key={f} variant={memberFilter === f ? 'default' : 'outline'} size="sm"
                    onClick={() => setMemberFilter(f)} className="text-xs h-7"
                    data-testid={`member-filter-${f || 'all'}`}
                  >
                    {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
                  </Button>
                ))}
              </div>
              <div className="divide-y">
                {members.map((m) => {
                  const access = getMemberAccessLabel(m);
                  return (
                    <div key={m.user_id} className="px-4 py-3 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-[#0A192F] flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {m.name?.charAt(0) || 'U'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                          <p className="text-xs text-slate-400">{m.email} &middot; {m.user_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`${access.color} text-[10px]`}>{access.label}</Badge>
                        {m.granted_access?.active ? (
                          <Button variant="outline" size="sm" className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleRevokeAccess(m.user_id)} data-testid={`revoke-${m.user_id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Revoke
                          </Button>
                        ) : m.membership === 'free' ? (
                          <Button variant="outline" size="sm" className="text-xs h-7 text-green-700 border-green-200 hover:bg-green-50"
                            onClick={() => setShowGrantAccess(m.user_id)} data-testid={`grant-${m.user_id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Grant Access
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {members.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No members found</p>}
              </div>
            </div>
          </TabsContent>

          {/* AUDIT LOG TAB */}
          <TabsContent value="audit">
            <div className="bg-white border border-slate-200">
              <div className="divide-y">
                {auditLogs.map((log) => (
                  <div key={log.log_id} className="px-4 py-3 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0 mt-0.5">
                      <Activity className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800">
                        <span className="font-semibold">{log.actor_name}</span>{' '}
                        <span className="text-slate-500">{log.action.replace(/_/g, ' ')}</span>
                        {log.target_id && <span className="text-slate-400 text-xs ml-1">({log.target_id})</span>}
                      </p>
                      {log.details && <p className="text-xs text-slate-400 mt-0.5">{log.details}</p>}
                      <p className="text-xs text-slate-300 mt-0.5">{formatDate(log.created_at)}</p>
                    </div>
                  </div>
                ))}
                {auditLogs.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No audit logs yet</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ========== CREATE POST MODAL ========== */}
      {showCreatePost && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl" data-testid="create-post-modal">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#0A192F]">Create Premium Post</h2>
              <button onClick={() => setShowCreatePost(false)} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Title *</Label>
                <Input value={postForm.title} onChange={e => setPostForm({ ...postForm, title: e.target.value })}
                  placeholder="e.g., Gold Bars 99.99% - Direct Allocation" className="mt-1" data-testid="post-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={postForm.category} onValueChange={v => setPostForm({ ...postForm, category: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Post Type</Label>
                  <Select value={postForm.post_type} onValueChange={v => setPostForm({ ...postForm, post_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {POST_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea value={postForm.content} onChange={e => setPostForm({ ...postForm, content: e.target.value })}
                  placeholder="Detailed description of the deal, mandate, or intelligence..." rows={6} className="mt-1" data-testid="post-content-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Deal Value</Label>
                  <Input value={postForm.deal_value} onChange={e => setPostForm({ ...postForm, deal_value: e.target.value })}
                    placeholder="$5M" className="mt-1" />
                </div>
                <div>
                  <Label>Origin</Label>
                  <Input value={postForm.origin} onChange={e => setPostForm({ ...postForm, origin: e.target.value })}
                    placeholder="Switzerland" className="mt-1" />
                </div>
                <div>
                  <Label>Destination</Label>
                  <Input value={postForm.destination} onChange={e => setPostForm({ ...postForm, destination: e.target.value })}
                    placeholder="Dubai, UAE" className="mt-1" />
                </div>
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input value={postForm.tags} onChange={e => setPostForm({ ...postForm, tags: e.target.value })}
                  placeholder="gold, LBMA, mandate, CIF" className="mt-1" />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900">Premium Content</p>
                  <p className="text-xs text-slate-500">Lock this post for non-premium users</p>
                </div>
                <Switch checked={postForm.is_premium} onCheckedChange={v => setPostForm({ ...postForm, is_premium: v })} />
              </div>

              {/* Post Labels */}
              <div>
                <Label>Post Labels</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {POST_LABELS.map(label => {
                    const active = postForm.labels.includes(label.id);
                    return (
                      <button
                        key={label.id}
                        type="button"
                        onClick={() => setPostForm(prev => ({
                          ...prev,
                          labels: active ? prev.labels.filter(l => l !== label.id) : [...prev.labels, label.id]
                        }))}
                        className={`px-3 py-1.5 rounded text-xs font-semibold border transition-all ${active ? label.color + ' border-current' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                        data-testid={`label-${label.id}`}
                      >
                        {label.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notification Controls */}
              <div className="border border-blue-100 bg-blue-50/30 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-[#0A192F]">Notification Controls</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700">Send Notification</p>
                    <p className="text-xs text-slate-500">Alert premium members about this post</p>
                  </div>
                  <Switch checked={postForm.send_notification} onCheckedChange={v => setPostForm({ ...postForm, send_notification: v })} data-testid="toggle-send-notif" />
                </div>
                {postForm.send_notification && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700">Priority Alert</p>
                        <p className="text-xs text-slate-500">Highlight as urgent notification</p>
                      </div>
                      <Switch checked={postForm.priority_alert} onCheckedChange={v => setPostForm({ ...postForm, priority_alert: v })} data-testid="toggle-priority" />
                    </div>
                    <div>
                      <Label className="text-xs">Audience</Label>
                      <Select value={postForm.notify_audience} onValueChange={v => setPostForm({ ...postForm, notify_audience: v })}>
                        <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (based on deal value & labels)</SelectItem>
                          <SelectItem value="all_premium">All Premium Members</SelectItem>
                          <SelectItem value="category_subscribers">Category Subscribers Only</SelectItem>
                          <SelectItem value="high_value_only">High-Value Alert Subscribers Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <p className="text-[10px] text-slate-400">Note: Posts with deal values above $10M or priority labels auto-trigger notifications even without this toggle.</p>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreatePost(false)}>Cancel</Button>
              <Button className="bg-[#EA580C] hover:bg-[#C2410C] text-white" disabled={postSubmitting} onClick={handleCreatePost} data-testid="submit-post-btn">
                <Send className="w-4 h-4 mr-1" /> {postSubmitting ? 'Publishing...' : 'Publish Post'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== GRANT ACCESS MODAL ========== */}
      {showGrantAccess && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-md rounded-lg shadow-2xl" data-testid="grant-access-modal">
            <div className="p-5 border-b">
              <h2 className="font-bold text-lg text-[#0A192F]">Grant Premium Access</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Access Duration</Label>
                <Select value={grantForm.access_type} onValueChange={v => setGrantForm({ ...grantForm, access_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lifetime">Lifetime</SelectItem>
                    <SelectItem value="30_days">30 Days</SelectItem>
                    <SelectItem value="90_days">90 Days</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {grantForm.access_type === 'custom' && (
                <div>
                  <Label>Custom Days</Label>
                  <Input type="number" value={grantForm.custom_days} onChange={e => setGrantForm({ ...grantForm, custom_days: parseInt(e.target.value) || 0 })}
                    placeholder="60" className="mt-1" />
                </div>
              )}
              <div>
                <Label>Reason (optional)</Label>
                <Input value={grantForm.reason} onChange={e => setGrantForm({ ...grantForm, reason: e.target.value })}
                  placeholder="VIP partner, trial, etc." className="mt-1" />
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowGrantAccess(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleGrantAccess(showGrantAccess)} data-testid="confirm-grant-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> Grant Access
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== INVITE ADMIN MODAL ========== */}
      {showInvite && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-md rounded-lg shadow-2xl" data-testid="invite-admin-modal">
            <div className="p-5 border-b">
              <h2 className="font-bold text-lg text-[#0A192F]">Invite Admin / Editor</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Email</Label>
                <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="editor@company.com" className="mt-1" data-testid="invite-email-input" />
              </div>
              <div className="space-y-3">
                <Label>Permissions</Label>
                {[
                  { key: 'can_post', label: 'Can Create & Edit Posts' },
                  { key: 'can_manage_users', label: 'Can Manage Members' },
                  { key: 'can_grant_access', label: 'Can Grant/Revoke Access' },
                ].map(p => (
                  <div key={p.key} className="flex items-center justify-between p-2 rounded bg-slate-50">
                    <span className="text-sm text-slate-700">{p.label}</span>
                    <Switch checked={invitePerms[p.key]} onCheckedChange={v => setInvitePerms({ ...invitePerms, [p.key]: v })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
              <Button className="bg-[#0A192F] hover:bg-[#172A46] text-white" onClick={handleInviteAdmin} data-testid="send-invite-btn">
                <Mail className="w-4 h-4 mr-1" /> Send Invite
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelAdminPage;
